/**
 * Przeniesienie kadry z innego programu (Etap 55, ADR-0061) — odczyt plików.
 *
 * Trzy pliki CSV: pracownicy (wymagany), historia płac i zwolnienia
 * (opcjonalne). Moduł jest czysty: parsuje, normalizuje i nazywa błędy przy
 * polu. Reguły z bazy (adres z kontem, numer PESEL już w aktach) i reguły
 * między plikami, które wymagają miesiąca przejścia, dokłada serwer.
 *
 * Kwoty historii są danymi z poprzedniego programu — nie przeliczamy ich.
 * Sprawdzamy tylko spójność wewnątrz wiersza i ostrzegamy, nie odrzucamy.
 */
import { isEmploymentContract, type ContractType } from './enums'
import { normalizeKey, parseContractType, parseCsv, parseImportDate, utf8Length } from './invitation-import'
import { KUP_VARIANTS, PPK_STATUSES, TAX_CREDIT_DIVISORS, type KupVariant, type PpkStatus } from './payroll'
import { isNfzBranchCode, parseStreetLine } from './registrations'
import { ABSENCE_BENEFIT_TYPES, type AbsenceBenefitType } from './sickness'
import { isTaxOfficeCode } from './tax-offices'
import { emailSchema, ibanPlSchema, isValidPesel, phonePlSchema, postalCodeSchema } from './validators'

export const STAFF_MIGRATION_MAX_ROWS = 500
export const STAFF_MIGRATION_MAX_BYTES = 2 * 1024 * 1024

export type MigrationFieldError =
  | 'required'
  | 'invalid'
  | 'email'
  | 'pesel'
  | 'iban'
  | 'date'
  | 'month'
  | 'amount'
  | 'contractType'
  | 'taxOffice'
  | 'nfz'
  | 'address'
  | 'postalCode'
  | 'phone'
  | 'duplicateInFile'
  | 'unknownPesel'
  | 'afterCutover'
  | 'tooEarly'
  | 'beforeHire'
  /* Reguły z bazy — dokłada je serwer. */
  | 'emailTaken'
  | 'peselTaken'

export type MigrationWarning = 'taxBaseMismatch' | 'netMismatch' | 'socialBaseAboveGross'

export interface MigrationRow<Values> {
  readonly line: number
  readonly values: Values
  readonly errors: Array<{ field: string; code: MigrationFieldError }>
  readonly warnings: Array<{ field: string; code: MigrationWarning }>
}

export type MigrationFileError =
  | { code: 'empty' }
  | { code: 'tooLarge' }
  | { code: 'tooManyRows'; max: number }
  | { code: 'missingColumns'; columns: string[] }
  | { code: 'unclosedQuote'; line: number }

export interface MigrationFileParsed<Values> {
  readonly rows: MigrationRow<Values>[]
  readonly fileErrors: MigrationFileError[]
  readonly ignoredColumns: string[]
}

/* ── Wartości ──────────────────────────────────────────────────────────── */

/** Kwota w złotych z arkusza („1 234,56”, „1234.56”, „1 234 zł”) → grosze; `null` = nieczytelna. */
export function parseAmountGr(value: string): number | null {
  const compact = value.replace(/[\s  ]/g, '').replace(/zł$/i, '')
  if (!/^-?\d+([.,]\d{1,2})?$/.test(compact)) return null
  const [whole, fraction = ''] = compact.replace(',', '.').split('.')
  const negative = whole!.startsWith('-')
  const grosze = Math.abs(Number(whole)) * 100 + Number(fraction.padEnd(2, '0'))
  return negative ? -grosze : grosze
}

/** Liczba dziesiętna z przecinkiem albo kropką (dni, godziny, etat). */
export function parseDecimal(value: string): number | null {
  const compact = value.replace(/[\s ]/g, '').replace(',', '.')
  if (!/^\d+(\.\d+)?$/.test(compact)) return null
  return Number(compact)
}

/** Etat: „1”, „1,0”, „0,5”, „1/2”, „3/4”. */
export function parseFte(value: string): number | null {
  const fraction = /^(\d+)\s*\/\s*(\d+)$/.exec(value.trim())
  const fte = fraction ? Number(fraction[1]) / Number(fraction[2]) : parseDecimal(value)
  return fte !== null && Number.isFinite(fte) && fte > 0 && fte <= 1 ? Math.round(fte * 100) / 100 : null
}

/** Stawka procentowa („2”, „1,5”, „2 %”) → punkty bazowe. */
export function parseRateBp(value: string): number | null {
  const number = parseDecimal(value.replace('%', ''))
  return number === null || number > 100 ? null : Math.round(number * 100)
}

function parseBoolean(value: string): boolean | null {
  const key = normalizeKey(value)
  if (['tak', 't', 'yes', 'y', '1', 'true', 'x'].includes(key)) return true
  if (['nie', 'n', 'no', '0', 'false'].includes(key)) return false
  return null
}

/** Miesiąc „rrrr-mm”, „mm/rrrr”, „mm.rrrr” → „rrrr-mm”. */
export function parseMonth(value: string): string | null {
  const iso = /^(\d{4})-(\d{1,2})$/.exec(value.trim())
  const local = /^(\d{1,2})[./](\d{4})$/.exec(value.trim())
  const [year, month] = iso ? [iso[1]!, iso[2]!] : local ? [local[2]!, local[1]!] : ['', '']
  const number = Number(month)
  return year && number >= 1 && number <= 12 ? `${year}-${String(number).padStart(2, '0')}` : null
}

/* ── Wspólny odczyt pliku ─────────────────────────────────────────────── */

interface ColumnSpec<Field extends string> {
  readonly aliases: Readonly<Record<Field, readonly string[]>>
  readonly required: readonly Field[]
}

interface RawRow<Field extends string> {
  readonly line: number
  readonly cells: Partial<Record<Field, string>>
}

function readFile<Field extends string>(
  text: string,
  spec: ColumnSpec<Field>,
): { rows: RawRow<Field>[]; fileErrors: MigrationFileError[]; ignoredColumns: string[] } {
  if (utf8Length(text) > STAFF_MIGRATION_MAX_BYTES) return { rows: [], fileErrors: [{ code: 'tooLarge' }], ignoredColumns: [] }
  const { rows: raw, unclosedQuoteLine } = parseCsv(text)
  if (unclosedQuoteLine !== null) {
    return { rows: [], fileErrors: [{ code: 'unclosedQuote', line: unclosedQuoteLine }], ignoredColumns: [] }
  }
  const [header, ...body] = raw
  if (!header || header.every((cell) => cell.trim() === '')) {
    return { rows: [], fileErrors: [{ code: 'empty' }], ignoredColumns: [] }
  }
  const byAlias = new Map<string, Field>()
  for (const [field, aliases] of Object.entries(spec.aliases) as Array<[Field, readonly string[]]>) {
    for (const alias of aliases) byAlias.set(alias, field)
  }
  const columns = header.map((cell) => byAlias.get(normalizeKey(cell)) ?? null)
  const ignoredColumns = header.filter((cell, index) => columns[index] === null && cell.trim() !== '')
  const missing = spec.required.filter((field) => !columns.includes(field))
  if (missing.length > 0) return { rows: [], fileErrors: [{ code: 'missingColumns', columns: [...missing] }], ignoredColumns }

  const fileErrors: MigrationFileError[] = []
  const rows = body
    .map((cells, index) => ({ cells, line: index + 2 }))
    .filter(({ cells }) => cells.some((cell) => cell.trim() !== ''))
    .map(({ cells, line }) => {
      const out: Partial<Record<Field, string>> = {}
      columns.forEach((field, index) => {
        const value = (cells[index] ?? '').trim()
        if (field !== null && value !== '') out[field] = value
      })
      return { line, cells: out }
    })
  if (rows.length === 0) fileErrors.push({ code: 'empty' })
  if (rows.length > STAFF_MIGRATION_MAX_ROWS) fileErrors.push({ code: 'tooManyRows', max: STAFF_MIGRATION_MAX_ROWS })
  return { rows, fileErrors, ignoredColumns }
}

/** Pomocnik wiersza: wymagane pola i odczyt wartości z kodem błędu przy polu. */
function rowReader<Field extends string>(row: RawRow<Field>, required: readonly Field[]) {
  const errors: Array<{ field: string; code: MigrationFieldError }> = []
  for (const field of required) if (row.cells[field] === undefined) errors.push({ field, code: 'required' })
  function read<T>(field: Field, parse: (value: string) => T | null, code: MigrationFieldError): T | undefined {
    const value = row.cells[field]
    if (value === undefined) return undefined
    const parsed = parse(value)
    if (parsed === null) {
      errors.push({ field, code })
      return undefined
    }
    return parsed
  }
  return { errors, read, text: (field: Field) => row.cells[field] }
}

/* ── Pracownicy ───────────────────────────────────────────────────────── */

const EMPLOYEE_ALIASES = {
  firstName: ['imie', 'first_name'],
  lastName: ['nazwisko', 'last_name'],
  email: ['email', 'e_mail', 'adres_email'],
  pesel: ['pesel'],
  birthDate: ['data_urodzenia'],
  addressStreet: ['adres', 'ulica', 'ulica_i_numer'],
  addressCityZip: ['kod_i_miasto', 'kod_i_miejscowosc', 'kod_miasto', 'miejscowosc'],
  phone: ['telefon'],
  iban: ['rachunek', 'iban', 'nr_rachunku', 'numer_rachunku'],
  bankName: ['bank'],
  taxOfficeCode: ['urzad_skarbowy', 'kod_urzedu', 'kod_us', 'us'],
  nfzBranch: ['nfz', 'oddzial_nfz'],
  position: ['stanowisko'],
  departmentName: ['dzial'],
  workplace: ['miejsce_pracy'],
  contractType: ['rodzaj_umowy', 'umowa'],
  hiredAt: ['data_zatrudnienia'],
  fte: ['etat', 'wymiar_etatu'],
  baseSalary: ['wynagrodzenie', 'wynagrodzenie_zasadnicze'],
  bonus: ['premia_stala'],
  pit2: ['pit2', 'pit_2'],
  kupVariant: ['koszty', 'koszty_uzyskania'],
  ppkStatus: ['ppk'],
  ppkEmployeeRate: ['ppk_pracownik'],
  ppkEmployerRate: ['ppk_pracodawca'],
  insuranceCode: ['kod_tytulu', 'kod_tytulu_ubezpieczenia'],
  occupationCode: ['kod_zawodu'],
  leaveBaseDays: ['wymiar_urlopu'],
  leaveCarryoverDays: ['urlop_zalegly'],
  leaveUsedDays: ['urlop_wykorzystany'],
  overtimeHours: ['nadgodziny_w_roku'],
  sickPayDays: ['dni_chorobowego', 'dni_wynagrodzenia_chorobowego'],
  previousEmployeeNo: ['nr_akt', 'numer_akt'],
} as const
export type MigrationEmployeeField = keyof typeof EMPLOYEE_ALIASES
export const MIGRATION_EMPLOYEE_FIELDS = Object.keys(EMPLOYEE_ALIASES) as MigrationEmployeeField[]
const EMPLOYEE_REQUIRED: readonly MigrationEmployeeField[] = [
  'firstName',
  'lastName',
  'email',
  'pesel',
  'birthDate',
  'addressStreet',
  'addressCityZip',
  'iban',
  'taxOfficeCode',
  'nfzBranch',
  'position',
  'contractType',
  'hiredAt',
  'baseSalary',
]

export interface MigrationEmployee {
  firstName?: string
  lastName?: string
  email?: string
  pesel?: string
  birthDate?: string
  addressStreet?: string
  addressCityZip?: string
  phone?: string
  iban?: string
  bankName?: string
  taxOfficeCode?: string
  nfzBranch?: string
  position?: string
  departmentName?: string
  workplace?: string
  contractType?: ContractType
  hiredAt?: string
  fte?: number
  baseSalaryGr?: number
  bonusGr?: number
  taxCreditDivisor?: (typeof TAX_CREDIT_DIVISORS)[number]
  kupVariant?: KupVariant
  ppkStatus?: PpkStatus
  ppkEmployeeBp?: number
  ppkEmployerBp?: number
  insuranceCode?: string
  occupationCode?: string
  leaveBaseDays?: 20 | 26
  leaveCarryoverDays?: number
  leaveUsedDays?: number
  overtimeHours?: number
  sickPayDays?: number
  previousEmployeeNo?: string
}

/** PIT-2: „tak” = 1/12 kwoty zmniejszającej (art. 32 ust. 3), „nie” = 0, albo wprost 12/24/36. */
function parsePit2(value: string): (typeof TAX_CREDIT_DIVISORS)[number] | null {
  const flag = parseBoolean(value)
  if (flag !== null) return flag ? 12 : 0
  const number = Number(value.trim())
  return (TAX_CREDIT_DIVISORS as readonly number[]).includes(number) ? (number as (typeof TAX_CREDIT_DIVISORS)[number]) : null
}

function parseKup(value: string): KupVariant | null {
  const key = normalizeKey(value)
  if (key.startsWith('podw')) return 'podwyzszone'
  if (key.startsWith('podst')) return 'podstawowe'
  return (KUP_VARIANTS as readonly string[]).includes(key) ? (key as KupVariant) : null
}

function parsePpk(value: string): PpkStatus | null {
  const key = normalizeKey(value)
  if (['tak', 'uczestnik', 'uczestniczy'].includes(key)) return 'uczestnik'
  if (['nie', 'rezygnacja', 'zrezygnowal', 'zrezygnowala'].includes(key)) return 'rezygnacja'
  if (['brak', 'brak_deklaracji'].includes(key)) return 'brak_deklaracji'
  return (PPK_STATUSES as readonly string[]).includes(key) ? (key as PpkStatus) : null
}

export function parseMigrationEmployees(text: string): MigrationFileParsed<MigrationEmployee> {
  const file = readFile(text, { aliases: EMPLOYEE_ALIASES, required: EMPLOYEE_REQUIRED })
  const rows = file.rows.map((row): MigrationRow<MigrationEmployee> => {
    const { errors, read, text: cell } = rowReader(row, EMPLOYEE_REQUIRED)
    const values: MigrationEmployee = {}
    for (const field of ['firstName', 'lastName', 'position', 'departmentName', 'workplace', 'bankName', 'previousEmployeeNo'] as const) {
      const value = cell(field)
      if (value !== undefined) values[field] = value
    }
    const email = read('email', (value) => (emailSchema.safeParse(value).success ? emailSchema.parse(value) : null), 'email')
    if (email) values.email = email
    const pesel = read('pesel', (value) => (isValidPesel(value.trim()) ? value.trim() : null), 'pesel')
    if (pesel) values.pesel = pesel
    const birthDate = read('birthDate', parseImportDate, 'date')
    if (birthDate) values.birthDate = birthDate
    const street = read('addressStreet', (value) => (parseStreetLine(value) !== null ? value : null), 'address')
    if (street) values.addressStreet = street
    const cityZip = read(
      'addressCityZip',
      (value) => (postalCodeSchema.safeParse(value.split(/\s+/)[0] ?? '').success ? value : null),
      'postalCode',
    )
    if (cityZip) values.addressCityZip = cityZip
    const phone = read('phone', (value) => {
      const parsed = phonePlSchema.safeParse(value)
      return parsed.success ? parsed.data : null
    }, 'phone')
    if (phone) values.phone = phone
    const iban = read('iban', (value) => {
      const parsed = ibanPlSchema.safeParse(value)
      return parsed.success ? parsed.data : null
    }, 'iban')
    if (iban) values.iban = iban
    const taxOffice = read('taxOfficeCode', (value) => (isTaxOfficeCode(value.trim()) ? value.trim() : null), 'taxOffice')
    if (taxOffice) values.taxOfficeCode = taxOffice
    const nfz = read('nfzBranch', (value) => (isNfzBranchCode(value.trim().toUpperCase()) ? value.trim().toUpperCase() : null), 'nfz')
    if (nfz) values.nfzBranch = nfz
    const contract = read('contractType', parseContractType, 'contractType')
    if (contract) values.contractType = contract
    const hiredAt = read('hiredAt', parseImportDate, 'date')
    if (hiredAt) values.hiredAt = hiredAt
    const fte = read('fte', parseFte, 'invalid')
    if (fte !== undefined) values.fte = fte
    const salary = read('baseSalary', (value) => {
      const amount = parseAmountGr(value)
      return amount !== null && amount > 0 ? amount : null
    }, 'amount')
    if (salary !== undefined) values.baseSalaryGr = salary
    const bonus = read('bonus', (value) => {
      const amount = parseAmountGr(value)
      return amount !== null && amount >= 0 ? amount : null
    }, 'amount')
    if (bonus !== undefined) values.bonusGr = bonus
    const pit2 = read('pit2', parsePit2, 'invalid')
    if (pit2 !== undefined) values.taxCreditDivisor = pit2
    const kup = read('kupVariant', parseKup, 'invalid')
    if (kup) values.kupVariant = kup
    const ppk = read('ppkStatus', parsePpk, 'invalid')
    if (ppk) values.ppkStatus = ppk
    const ppkEmployee = read('ppkEmployeeRate', parseRateBp, 'invalid')
    if (ppkEmployee !== undefined) values.ppkEmployeeBp = ppkEmployee
    const ppkEmployer = read('ppkEmployerRate', parseRateBp, 'invalid')
    if (ppkEmployer !== undefined) values.ppkEmployerBp = ppkEmployer
    const insurance = read('insuranceCode', (value) => (/^\d{6}$/.test(value.replace(/\s/g, '')) ? value.replace(/\s/g, '') : null), 'invalid')
    if (insurance) values.insuranceCode = insurance
    const occupation = read('occupationCode', (value) => (/^\d{6}$/.test(value.trim()) ? value.trim() : null), 'invalid')
    if (occupation) values.occupationCode = occupation
    const base = read('leaveBaseDays', (value) => (value.trim() === '20' ? 20 : value.trim() === '26' ? 26 : null), 'invalid')
    if (base !== undefined) values.leaveBaseDays = base
    for (const [field, key] of [
      ['leaveCarryoverDays', 'leaveCarryoverDays'],
      ['leaveUsedDays', 'leaveUsedDays'],
      ['overtimeHours', 'overtimeHours'],
      ['sickPayDays', 'sickPayDays'],
    ] as const) {
      const number = read(field, parseDecimal, 'invalid')
      if (number !== undefined) values[key] = number
    }
    if (values.sickPayDays !== undefined && !Number.isInteger(values.sickPayDays)) {
      errors.push({ field: 'sickPayDays', code: 'invalid' })
    }
    if (values.birthDate && values.pesel && !peselMatchesBirthDate(values.pesel, values.birthDate)) {
      errors.push({ field: 'birthDate', code: 'invalid' })
    }
    return { line: row.line, values, errors, warnings: [] }
  })
  markDuplicates(rows, (row) => row.values.pesel, 'pesel')
  markDuplicates(rows, (row) => row.values.email, 'email')
  return { rows, fileErrors: file.fileErrors, ignoredColumns: file.ignoredColumns }
}

/** Data urodzenia z numeru PESEL (stulecie w miesiącu) musi zgadzać się z kolumną. */
function peselMatchesBirthDate(pesel: string, birthDate: string): boolean {
  const year = Number(pesel.slice(0, 2))
  let month = Number(pesel.slice(2, 4))
  const day = pesel.slice(4, 6)
  const century = month > 80 ? 1800 : month > 60 ? 2200 : month > 40 ? 2100 : month > 20 ? 2000 : 1900
  month %= 20
  return `${century + year}-${String(month).padStart(2, '0')}-${day}` === birthDate
}

function markDuplicates<Values>(
  rows: MigrationRow<Values>[],
  key: (row: MigrationRow<Values>) => string | undefined,
  field: string,
): void {
  const seen = new Map<string, MigrationRow<Values>[]>()
  for (const row of rows) {
    const value = key(row)
    if (value !== undefined) seen.set(value, [...(seen.get(value) ?? []), row])
  }
  for (const group of seen.values()) {
    if (group.length > 1) for (const row of group) row.errors.push({ field, code: 'duplicateInFile' })
  }
}

/* ── Historia płac ────────────────────────────────────────────────────── */

const HISTORY_AMOUNTS = {
  grossGr: ['przychod', 'brutto'],
  basePayGr: ['wynagrodzenie_zasadnicze', 'skladniki_stale'],
  variablePayGr: ['skladniki_zmienne', 'premie'],
  kupGr: ['koszty', 'koszty_uzyskania'],
  authorKupGr: ['koszty_autorskie'],
  pensionEmployeeGr: ['emerytalna_pracownik', 'skladka_emerytalna_pracownik'],
  disabilityEmployeeGr: ['rentowa_pracownik', 'skladka_rentowa_pracownik'],
  sicknessEmployeeGr: ['chorobowa_pracownik', 'skladka_chorobowa'],
  healthGr: ['zdrowotna', 'skladka_zdrowotna'],
  socialBaseGr: ['podstawa_emerytalna', 'podstawa_skladek'],
  taxBaseGr: ['dochod', 'podstawa_opodatkowania'],
  taxCreditGr: ['kwota_zmniejszajaca'],
  taxAdvanceGr: ['zaliczka', 'zaliczka_pit'],
  youthReliefGr: ['zwolnienie_mlodzi', 'ulga_dla_mlodych'],
  taxReliefGr: ['zwolnienie_ulgi', 'ulga_152_154'],
  ppkEmployeeGr: ['ppk_pracownik'],
  ppkEmployerGr: ['ppk_pracodawca'],
  pensionEmployerGr: ['emerytalna_pracodawca'],
  disabilityEmployerGr: ['rentowa_pracodawca'],
  accidentGr: ['wypadkowa'],
  laborFundGr: ['fp', 'fundusz_pracy', 'fundusz_solidarnosciowy'],
  guaranteedBenefitsGr: ['fgsp'],
  sickPayGr: ['wynagrodzenie_chorobowe'],
  benefitGr: ['zasilki', 'zasilek'],
  netGr: ['do_wyplaty', 'netto'],
} as const
type HistoryAmountField = keyof typeof HISTORY_AMOUNTS
const HISTORY_ALIASES = {
  pesel: ['pesel'],
  settlementMonth: ['miesiac_wyplaty'],
  workedMonth: ['miesiac_przepracowany', 'za_miesiac'],
  payoutDate: ['data_wyplaty'],
  contractType: ['rodzaj_umowy', 'umowa'],
  sickDays: ['dni_chorobowego'],
  benefitDays: ['dni_zasilku'],
  ...HISTORY_AMOUNTS,
} as const
export type MigrationHistoryField = keyof typeof HISTORY_ALIASES
const HISTORY_REQUIRED: readonly MigrationHistoryField[] = [
  'pesel',
  'settlementMonth',
  'workedMonth',
  'grossGr',
  'kupGr',
  'pensionEmployeeGr',
  'disabilityEmployeeGr',
  'sicknessEmployeeGr',
  'healthGr',
  'socialBaseGr',
  'taxAdvanceGr',
  'pensionEmployerGr',
  'disabilityEmployerGr',
  'accidentGr',
]

export type MigrationHistory = { pesel?: string; settlementMonth?: string; workedMonth?: string; payoutDate?: string; contractType?: ContractType; sickDays?: number; benefitDays?: number } & Partial<Record<HistoryAmountField, number>>

export function parseMigrationHistory(text: string): MigrationFileParsed<MigrationHistory> {
  const file = readFile(text, { aliases: HISTORY_ALIASES, required: HISTORY_REQUIRED })
  const rows = file.rows.map((row): MigrationRow<MigrationHistory> => {
    const { errors, read } = rowReader(row, HISTORY_REQUIRED)
    const warnings: MigrationRow<MigrationHistory>['warnings'] = []
    const values: MigrationHistory = {}
    const pesel = read('pesel', (value) => (isValidPesel(value.trim()) ? value.trim() : null), 'pesel')
    if (pesel) values.pesel = pesel
    const settlement = read('settlementMonth', parseMonth, 'month')
    if (settlement) values.settlementMonth = settlement
    const worked = read('workedMonth', parseMonth, 'month')
    if (worked) values.workedMonth = worked
    const payout = read('payoutDate', parseImportDate, 'date')
    if (payout) values.payoutDate = payout
    const contract = read('contractType', parseContractType, 'contractType')
    if (contract) values.contractType = contract
    for (const field of ['sickDays', 'benefitDays'] as const) {
      const days = read(field, (value) => (/^\d+$/.test(value.trim()) ? Number(value.trim()) : null), 'invalid')
      if (days !== undefined) values[field] = days
    }
    for (const field of Object.keys(HISTORY_AMOUNTS) as HistoryAmountField[]) {
      const amount = read(field, parseAmountGr, 'amount')
      if (amount !== undefined) values[field] = amount
    }
    if (payout && settlement && payout.slice(0, 7) !== settlement) errors.push({ field: 'payoutDate', code: 'invalid' })

    /* Spójność wewnątrz wiersza — ostrzeżenie, bo liczył to inny program. */
    const gross = values.grossGr
    if (gross !== undefined) {
      const social = (values.pensionEmployeeGr ?? 0) + (values.disabilityEmployeeGr ?? 0) + (values.sicknessEmployeeGr ?? 0)
      const exempt = (values.youthReliefGr ?? 0) + (values.taxReliefGr ?? 0)
      const expected = Math.max(0, gross - exempt - (values.kupGr ?? 0) - (values.authorKupGr ?? 0) - social)
      if (values.taxBaseGr !== undefined && Math.abs(values.taxBaseGr - expected) > 100) {
        warnings.push({ field: 'taxBaseGr', code: 'taxBaseMismatch' })
      }
      if (values.socialBaseGr !== undefined && values.socialBaseGr > gross) {
        warnings.push({ field: 'socialBaseGr', code: 'socialBaseAboveGross' })
      }
      const net = gross - social - (values.healthGr ?? 0) - (values.taxAdvanceGr ?? 0) - (values.ppkEmployeeGr ?? 0)
      if (values.netGr !== undefined && Math.abs(values.netGr - net) > 100) warnings.push({ field: 'netGr', code: 'netMismatch' })
    }
    return { line: row.line, values, errors, warnings }
  })
  /* Jeden wiersz na osobę, miesiąc wypłaty i miesiąc przepracowany. */
  markDuplicates(rows, (row) =>
    row.values.pesel && row.values.settlementMonth && row.values.workedMonth
      ? `${row.values.pesel}|${row.values.settlementMonth}|${row.values.workedMonth}|${row.values.contractType ?? ''}`
      : undefined,
  'settlementMonth')
  return { rows, fileErrors: file.fileErrors, ignoredColumns: file.ignoredColumns }
}

/** Kwoty wiersza historii uzupełnione tym, co da się wyprowadzić (dochód, netto, sumy). */
export function completeMigrationHistory(values: MigrationHistory): Required<Pick<MigrationHistory, HistoryAmountField>> {
  const amount = (field: HistoryAmountField) => values[field] ?? 0
  const social = amount('pensionEmployeeGr') + amount('disabilityEmployeeGr') + amount('sicknessEmployeeGr')
  const exempt = amount('youthReliefGr') + amount('taxReliefGr')
  const gross = amount('grossGr')
  const taxBase = values.taxBaseGr ?? Math.max(0, gross - exempt - amount('kupGr') - amount('authorKupGr') - social)
  const net = values.netGr ?? gross - social - amount('healthGr') - amount('taxAdvanceGr') - amount('ppkEmployeeGr')
  const variable = amount('variablePayGr')
  return {
    grossGr: gross,
    basePayGr: values.basePayGr ?? Math.max(0, gross - variable - amount('sickPayGr') - amount('benefitGr')),
    variablePayGr: variable,
    kupGr: amount('kupGr'),
    authorKupGr: amount('authorKupGr'),
    pensionEmployeeGr: amount('pensionEmployeeGr'),
    disabilityEmployeeGr: amount('disabilityEmployeeGr'),
    sicknessEmployeeGr: amount('sicknessEmployeeGr'),
    healthGr: amount('healthGr'),
    socialBaseGr: amount('socialBaseGr'),
    taxBaseGr: taxBase,
    taxCreditGr: amount('taxCreditGr'),
    taxAdvanceGr: amount('taxAdvanceGr'),
    youthReliefGr: amount('youthReliefGr'),
    taxReliefGr: amount('taxReliefGr'),
    ppkEmployeeGr: amount('ppkEmployeeGr'),
    ppkEmployerGr: amount('ppkEmployerGr'),
    pensionEmployerGr: amount('pensionEmployerGr'),
    disabilityEmployerGr: amount('disabilityEmployerGr'),
    accidentGr: amount('accidentGr'),
    laborFundGr: amount('laborFundGr'),
    guaranteedBenefitsGr: amount('guaranteedBenefitsGr'),
    sickPayGr: amount('sickPayGr'),
    benefitGr: amount('benefitGr'),
    netGr: net,
  }
}

/* ── Zwolnienia ───────────────────────────────────────────────────────── */

const SICK_ALIASES = {
  pesel: ['pesel'],
  dateFrom: ['od', 'data_od', 'poczatek'],
  dateTo: ['do', 'data_do', 'koniec'],
  benefitType: ['rodzaj', 'rodzaj_swiadczenia'],
  ezlaNumber: ['numer_zla', 'seria_i_numer', 'zla'],
} as const
export type MigrationSickField = keyof typeof SICK_ALIASES
const SICK_REQUIRED: readonly MigrationSickField[] = ['pesel', 'dateFrom', 'dateTo']

export interface MigrationSickLeave {
  pesel?: string
  dateFrom?: string
  dateTo?: string
  benefitType?: AbsenceBenefitType
  ezlaNumber?: string
}

function parseBenefitType(value: string): AbsenceBenefitType | null {
  const key = normalizeKey(value)
  if (key.startsWith('chor')) return 'chorobowe'
  if (key.startsWith('opiek')) return 'opiekuncze'
  if (key.startsWith('macierz')) return 'macierzynskie'
  return (ABSENCE_BENEFIT_TYPES as readonly string[]).includes(key) ? (key as AbsenceBenefitType) : null
}

export function parseMigrationSickLeaves(text: string): MigrationFileParsed<MigrationSickLeave> {
  const file = readFile(text, { aliases: SICK_ALIASES, required: SICK_REQUIRED })
  const rows = file.rows.map((row): MigrationRow<MigrationSickLeave> => {
    const { errors, read, text: cell } = rowReader(row, SICK_REQUIRED)
    const values: MigrationSickLeave = {}
    const pesel = read('pesel', (value) => (isValidPesel(value.trim()) ? value.trim() : null), 'pesel')
    if (pesel) values.pesel = pesel
    const from = read('dateFrom', parseImportDate, 'date')
    if (from) values.dateFrom = from
    const to = read('dateTo', parseImportDate, 'date')
    if (to) values.dateTo = to
    if (from && to && to < from) errors.push({ field: 'dateTo', code: 'date' })
    const type = read('benefitType', parseBenefitType, 'invalid')
    if (type) values.benefitType = type
    const ezla = cell('ezlaNumber')
    if (ezla) values.ezlaNumber = ezla
    return { line: row.line, values, errors, warnings: [] }
  })
  return { rows, fileErrors: file.fileErrors, ignoredColumns: file.ignoredColumns }
}

/* ── Reguły między plikami (bez bazy) ─────────────────────────────────── */

/** Najwcześniejszy miesiąc historii: styczeń roku przed rokiem przejścia (podstawa zasiłku z 12 miesięcy). */
export function earliestHistoryMonth(cutoverMonth: string): string {
  return `${Number(cutoverMonth.slice(0, 4)) - 1}-01`
}

/**
 * Reguły, które wymagają wszystkich plików i miesiąca przejścia: historia
 * i zwolnienia tylko dla osób z pliku pracowników, historia przed przejściem
 * i nie wcześniej niż rok przed nim, zatrudnienie przed przejściem. Zmienia
 * wiersze w miejscu (dopisuje błędy).
 */
export function crossCheckMigration(input: {
  cutoverMonth: string
  employees: MigrationFileParsed<MigrationEmployee>
  history?: MigrationFileParsed<MigrationHistory>
  sickLeaves?: MigrationFileParsed<MigrationSickLeave>
}): void {
  const people = new Map(
    input.employees.rows.filter((row) => row.values.pesel).map((row) => [row.values.pesel!, row.values]),
  )
  const cutoverDay = `${input.cutoverMonth}-01`
  for (const row of input.employees.rows) {
    if (row.values.hiredAt && row.values.hiredAt >= cutoverDay) row.errors.push({ field: 'hiredAt', code: 'afterCutover' })
  }
  const earliest = earliestHistoryMonth(input.cutoverMonth)
  for (const row of input.history?.rows ?? []) {
    const person = row.values.pesel ? people.get(row.values.pesel) : undefined
    if (row.values.pesel && !person) row.errors.push({ field: 'pesel', code: 'unknownPesel' })
    const month = row.values.settlementMonth
    if (month && month >= input.cutoverMonth) row.errors.push({ field: 'settlementMonth', code: 'afterCutover' })
    if (month && month < earliest) row.errors.push({ field: 'settlementMonth', code: 'tooEarly' })
    if (person?.hiredAt && row.values.workedMonth && `${row.values.workedMonth}-31` < person.hiredAt) {
      row.errors.push({ field: 'workedMonth', code: 'beforeHire' })
    }
  }
  for (const row of input.sickLeaves?.rows ?? []) {
    if (row.values.pesel && !people.has(row.values.pesel)) row.errors.push({ field: 'pesel', code: 'unknownPesel' })
    if (row.values.dateTo && row.values.dateTo >= cutoverDay) row.errors.push({ field: 'dateTo', code: 'afterCutover' })
  }
}

/** Czy wiersz pracownika jest umową o pracę — urlop, PIT-2 i etat mają sens tylko przy niej. */
export function migrationEmploymentContract(values: MigrationEmployee): boolean {
  return values.contractType !== undefined && isEmploymentContract(values.contractType)
}

/** Pole → nagłówek kolumny ze wzoru pliku: ekran pokazuje błąd pod nazwą, którą kadry widzą w arkuszu. */
export const MIGRATION_HEADERS = {
  employees: Object.fromEntries(Object.entries(EMPLOYEE_ALIASES).map(([field, list]) => [field, list[0]])),
  history: Object.fromEntries(Object.entries(HISTORY_ALIASES).map(([field, list]) => [field, list[0]])),
  sickLeaves: Object.fromEntries(Object.entries(SICK_ALIASES).map(([field, list]) => [field, list[0]])),
} as const satisfies Record<'employees' | 'history' | 'sickLeaves', Record<string, string>>

/** Szablony plików: same nagłówki, separator „;”, z BOM. */
export function staffMigrationTemplates(): { employees: string; history: string; sickLeaves: string } {
  const header = (aliases: Readonly<Record<string, readonly string[]>>) =>
    `﻿${Object.values(aliases).map((list) => list[0]).join(';')}\r\n`
  return { employees: header(EMPLOYEE_ALIASES), history: header(HISTORY_ALIASES), sickLeaves: header(SICK_ALIASES) }
}

